import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Briefcase, Building2, CornerDownLeft, FileText, Search } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { CaseStatusPill, QuoteStatusPill } from '@/components/ui/StatusPill'
import { cn } from '@/lib/cn'
import { duration, ease } from '@/lib/motion'
import {
  SEARCH_MIN_LENGTH,
  useGlobalSearch,
  type SearchResult,
  type SearchResultKind,
} from '@/features/search/api/useGlobalSearch'
import type { CaseStatus, QuoteStatus } from '@/types/domain'

/* Global search (UX-DR-23): one box over works, quotes and clients, opening a
 * result list under the field.
 *
 * A combobox rather than a search PAGE, because the question being asked here
 * is almost always "take me to AB-27" — an intermediate results page would put
 * a second click between the user and the record for no added information.
 * There is no submit: the list is the answer, and Enter takes the highlighted
 * row. That is also why the field carries no button; a search box that needs a
 * button to work suggests the typing alone did not do anything.
 *
 * ── On the motion here ────────────────────────────────────────────────────
 * The UX rule is blunt: "Motion is functional only. State transitions and
 * toasts. Nothing decorative." So every animation below reports a state the
 * user would otherwise have to infer, and none of them exist to be noticed:
 *
 *   · the field widens on focus — it is now the active surface, and the extra
 *     room is real, because the popover beneath it is that width;
 *   · the leading icon shifts to brand and the shortcut hint swaps for a live
 *     "clear" affordance — focus and content are different states;
 *   · the highlight SLIDES between rows (a shared layoutId) instead of
 *     blinking, which is what makes arrow-key movement legible as movement
 *     rather than as two unrelated rows lighting up;
 *   · rows stagger in by 14ms — enough to read the list as ordered by
 *     relevance, far too little to wait for;
 *   · the matched substring is tinted, so the reason a row is present is
 *     visible rather than inferred.
 *
 * All of it is gated on `useReducedMotion`. The CSS kill-switch in index.css
 * cannot reach Framer's rAF-driven inline styles, so the hook is the real gate
 * — the same reason LoginPage carries one.
 */

const KIND_ICONS: Record<SearchResultKind, LucideIcon> = {
  case: Briefcase,
  quote: FileText,
  client: Building2,
}

/** The status pill, chosen by kind — the two catalogues are different sets and
 *  a case's status must never be labelled from the quote catalogue. */
function ResultStatus({ result }: { result: SearchResult }) {
  if (!result.status) return null
  if (result.kind === 'case') {
    return <CaseStatusPill status={result.status as CaseStatus} />
  }
  if (result.kind === 'quote') {
    return <QuoteStatusPill status={result.status as QuoteStatus} />
  }
  return null
}

/* Tints the part of `text` the query actually matched.
 *
 * Plain-substring only, deliberately: the ranker normalises separators so
 * "ab27" can match "AB-27", and reconstructing which source characters that
 * collapsed form covered would be a second, subtly different matcher — one
 * that eventually disagrees with the one that chose the row. When the literal
 * query is not present the text renders unmarked, which is the honest outcome:
 * no claim is made about where the match was. */
function Highlighted({ text, query }: { text: string; query: string }) {
  const at = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return -1
    return text.toLowerCase().indexOf(needle)
  }, [text, query])

  if (at < 0) return <>{text}</>

  const end = at + query.trim().length
  return (
    <>
      {text.slice(0, at)}
      {/* Weight as well as colour: text-brand alone would be the only cue, and
          colour never carries meaning alone in this system. */}
      <mark className="bg-transparent text-brand font-emphasis">
        {text.slice(at, end)}
      </mark>
      {text.slice(end)}
    </>
  )
}

/** True on a Mac, where the hint reads ⌘K rather than Ctrl K. Read once at
 *  module scope — the platform does not change mid-session. */
const IS_MAC =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

export interface GlobalSearchProps {
  /** Hands the shell a way to focus this box, for the `/` and Cmd/Ctrl+K
   *  shortcuts it owns (UX-DR-23).
   *
   *  A callback rather than a ref prop: the shell wants the ACTION, not the
   *  element, and passing a ref would mean two components writing to one ref
   *  object — which React 19 no longer allows a child to do. Called with null
   *  on unmount so the shell drops the stale handle. */
  onRegisterFocus?: (focus: (() => void) | null) => void
  className?: string
}

/* Dismissal for this box's result list.
 *
 * Base UI: `Popover` considered and rejected. This is not an anchored popover
 * with a trigger — the input IS the surface, the list is a sibling that appears
 * while there is a query, and `Popover.Trigger` would make the field a button.
 * The kit's shared popover-dismissal hook, which this used to import, went with
 * the Select and DatePicker overlays that Base UI now positions; the three
 * listeners it held are inlined here, unchanged, because this is the one caller
 * left and a one-consumer hook in the kit is just a longer import path.
 *
 * The three listeners are not interchangeable and all three are load-bearing:
 *
 *   · `keydown` for Escape, bound on the DOCUMENT rather than the list. Focus
 *     can be inside the list on a non-focusable node, or back on the field, and
 *     Escape must close from either.
 *
 *   · `pointerdown` rather than `click` for outside dismissal. A click fires
 *     only on release, so a press that starts outside and drags in would leave
 *     the list open, and a press on another control would act on a page that
 *     still has a list over it.
 *
 *   · `focusin` for what pointers cannot reach — tabbing out of the last
 *     result, or focus moving programmatically. A list left open behind
 *     departed focus is a keyboard trap in slow motion.
 *
 * Escape returns focus to the field. Without that the keyboard is stranded on
 * `document.body` and the next Tab restarts at the top of the page; a dismissal
 * should put the user back where they opened from.
 */
function useDismissOnOutside({
  open,
  onClose,
  containerRef,
  triggerRef,
}: {
  open: boolean
  onClose: () => void
  /** The element that encloses both the field and the result list. */
  containerRef: RefObject<HTMLElement | null>
  triggerRef: RefObject<HTMLElement | null>
}) {
  useEffect(() => {
    if (!open) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      // Stop the Escape from continuing on to an enclosing overlay.
      event.stopPropagation()
      onClose()
      triggerRef.current?.focus()
    }

    const dismissOnOutside = (event: Event) => {
      const target = event.target
      if (target instanceof Node && containerRef.current?.contains(target)) return
      onClose()
    }

    document.addEventListener('keydown', onKeyDown, true)
    document.addEventListener('pointerdown', dismissOnOutside)
    document.addEventListener('focusin', dismissOnOutside)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.removeEventListener('pointerdown', dismissOnOutside)
      document.removeEventListener('focusin', dismissOnOutside)
    }
  }, [open, onClose, containerRef, triggerRef])
}

export function GlobalSearch({ onRegisterFocus, className }: GlobalSearchProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const reduceMotion = useReducedMotion()

  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [focused, setFocused] = useState(false)
  const [hovered, setHovered] = useState(false)
  const [highlight, setHighlight] = useState(0)

  const containerRef = useRef<HTMLDivElement>(null)
  const fieldRef = useRef<HTMLInputElement | null>(null)
  const listId = useId()

  /* Publishes the focus action to the shell for as long as this is mounted,
     and withdraws it on the way out so the shortcuts never call into a dead
     element. Selecting the existing text is what makes a second Cmd+K a
     REPLACE rather than an append — the box is a jump-to, not a document. */
  useEffect(() => {
    onRegisterFocus?.(() => fieldRef.current?.select())
    return () => onRegisterFocus?.(null)
  }, [onRegisterFocus])

  const { results, isLoading, isIdle } = useGlobalSearch(query, open)

  const close = useCallback(() => setOpen(false), [])

  useDismissOnOutside({
    open,
    onClose: close,
    containerRef,
    triggerRef: fieldRef,
  })

  /* Selecting a row leaves the box empty and closed. The alternative — keeping
     the query so the list can be reopened — means arriving at a record with
     its own name still sitting in the search field, which reads as a filter
     that is still applied to the page underneath. */
  const select = useCallback(
    (result: SearchResult) => {
      setOpen(false)
      setQuery('')
      fieldRef.current?.blur()
      navigate(result.to)
    },
    [navigate],
  )

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Escape clears before it closes: one press to abandon the query, a second
    // to leave the box. A single press that did both would throw away a long
    // query the user only wanted to correct.
    if (event.key === 'Escape') {
      if (query) {
        event.stopPropagation()
        setQuery('')
        return
      }
      return
    }

    if (!results.length) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setOpen(true)
      // Wraps, so holding the key cycles rather than sticking at the end.
      setHighlight((index) => (index + 1) % results.length)
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlight((index) => (index - 1 + results.length) % results.length)
      return
    }

    if (event.key === 'Enter') {
      const result = results[Math.min(highlight, results.length - 1)]
      if (!result) return
      event.preventDefault()
      select(result)
    }
  }

  const showPopover = open && !isIdle
  const activeId = results[highlight]
    ? `${listId}-${results[highlight].key}`
    : undefined

  /* The field's resting width, and what it grows to while in use. Expressed
     as a motion value rather than a Tailwind class so the change is animated
     — a width that jumps is a layout shift, the same width eased is the box
     accepting focus. */
  const fieldWidth = focused || query ? 380 : 300

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* The shell, not the <input>: the border and the icons live here, and
          the field inside is stripped bare. That is what lets the leading
          icon, the shortcut hint and the clear button sit in one box instead
          of being positioned over a control unaware of them.

          Focus is announced by the brand BORDER and the lift from the sunken
          tint to white — no halo, no shadow, exactly as control.ts defines it
          for every other control in the set. A search box that glowed while
          the selects beside it did not would read as a different control set,
          and only overlays may lift off the page. */}
      <motion.div
        animate={{
          width: fieldWidth,
          borderColor: focused
            ? 'var(--color-brand)'
            : hovered
              ? 'var(--color-border-default)'
              : 'var(--color-border-subtle)',
          backgroundColor:
            focused || hovered
              ? 'var(--color-surface)'
              : 'var(--color-surface-sunken)',
        }}
        initial={false}
        // Hover and focus are tracked in state rather than left to CSS
        // pseudo-classes because Framer writes borderColor as an INLINE style,
        // which a `hover:` utility could never override.
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
        transition={
          reduceMotion
            ? { duration: 0 }
            : { duration: duration.base, ease: ease.out }
        }
        className={cn(
          'flex h-[30px] max-w-full items-center gap-2 rounded-full border pr-1.5 pl-2.5',
          // 30px is CONTROL_HEIGHT.sm — the chrome size. The pill radius is the
          // one departure from the control set, and it is deliberate: this is
          // the only control in the topbar that is not part of a form, and the
          // shape is what says so before anything is read.
        )}
      >
        <Search
          size={14}
          aria-hidden
          className={cn(
            'shrink-0 transition-colors duration-[var(--dur-fast)]',
            focused ? 'text-brand' : 'text-text-faint',
          )}
        />

        <input
          ref={fieldRef}
          value={query}
          placeholder={t('common.search.placeholder')}
          aria-label={t('common.search.label')}
          onChange={(event) => {
            setQuery(event.target.value)
            setOpen(true)
            // Every keystroke re-ranks the list, so a highlight held at index 4
            // would point at an unrelated record. The top row is the best match.
            setHighlight(0)
          }}
          onFocus={() => {
            setFocused(true)
            setOpen(true)
          }}
          onBlur={() => setFocused(false)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded={showPopover}
          aria-controls={showPopover ? listId : undefined}
          aria-activedescendant={activeId}
          aria-autocomplete="list"
          autoComplete="off"
          className={cn(
            'min-w-0 flex-1 border-0 bg-transparent p-0 font-sans text-[13px]',
            'font-normal text-inherit outline-none',
            'placeholder:text-text-faint focus-visible:shadow-none',
          )}
        />

        {/* One slot, two states: the shortcut that opens the box when it is
            idle, the clear button once there is something to clear. Both are
            the same affordance — "this box is ready for something else" — so
            they cross-fade in place rather than one appearing beside the
            other. */}
        <AnimatePresence mode="wait" initial={false}>
          {query ? (
            <motion.button
              key="clear"
              type="button"
              // Not in the tab order: the field it clears is one Shift+Tab
              // away, and a keyboard user clears with a selection and Delete.
              tabIndex={-1}
              aria-label={t('common.form.clear')}
              onClick={() => {
                setQuery('')
                fieldRef.current?.focus()
              }}
              initial={reduceMotion ? false : { opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.8 }}
              transition={{ duration: duration.instant, ease: ease.out }}
              className={cn(
                'text-text-faint hover:text-text-body hover:bg-neutral-100',
                'flex size-5 shrink-0 cursor-pointer items-center justify-center',
                'rounded-full transition-colors duration-[var(--dur-fast)]',
              )}
            >
              <svg viewBox="0 0 14 14" className="size-3" aria-hidden>
                <path
                  d="M3.5 3.5l7 7m0-7l-7 7"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </motion.button>
          ) : (
            <motion.kbd
              key="hint"
              aria-hidden
              initial={reduceMotion ? false : { opacity: 0, scale: 0.9 }}
              animate={{ opacity: focused ? 0.5 : 1, scale: 1 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.9 }}
              transition={{ duration: duration.instant, ease: ease.out }}
              className={cn(
                'border-border-subtle text-text-faint hidden shrink-0 rounded-[5px]',
                'border px-1.5 py-0.5 font-sans text-[10px] leading-none md:block',
              )}
            >
              {IS_MAC ? '⌘K' : 'Ctrl K'}
            </motion.kbd>
          )}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {showPopover ? (
          <motion.div
            // 4px and a fade, matching menuVariants — the popover is already
            // attached to the field, so it needs no travel to say where it came
            // from. Slightly more than the menu's 2px because this panel is
            // taller and the same distance reads as less on a bigger surface.
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
            transition={{ duration: duration.fast, ease: ease.out }}
            className={cn(
              'bg-surface border-border-subtle absolute top-[calc(100%+6px)] left-0 z-40',
              'w-[min(420px,calc(100vw-2rem))] overflow-hidden rounded-lg border',
              'shadow-[var(--shadow-overlay)]',
            )}
          >
            {isLoading ? (
              <div className="flex items-center gap-2 px-3.5 py-3">
                {/* The spinner is the one piece of motion here that is not a
                    state CHANGE but a state CONTINUING — the established
                    exception, because a still frame cannot say "still
                    working". */}
                <span
                  aria-hidden
                  className="border-border-subtle border-t-brand size-3.5 animate-spin rounded-full border-[1.5px]"
                />
                <p className="text-text-muted text-[12px]">
                  {t('common.search.loading')}
                </p>
              </div>
            ) : results.length === 0 ? (
              <div className="px-3.5 py-3">
                <p className="text-text-body text-[12px] font-medium">
                  {t('common.search.emptyTitle')}
                </p>
                <p className="text-text-muted mt-0.5 text-[12px]">
                  {t('common.search.emptyDescription')}
                </p>
              </div>
            ) : (
              <>
                <ul
                  id={listId}
                  role="listbox"
                  aria-label={t('common.search.label')}
                  className="p-1"
                >
                  {results.map((result, index) => {
                    const Icon = KIND_ICONS[result.kind]
                    const active = index === highlight

                    return (
                      <motion.li
                        key={result.key}
                        initial={reduceMotion ? false : { opacity: 0, y: -3 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: duration.fast,
                          ease: ease.out,
                          // Ordered by relevance, so the list assembles in the
                          // order it should be read. Capped so a full ten rows
                          // still finish inside ~120ms.
                          delay: reduceMotion ? 0 : Math.min(index, 8) * 0.014,
                        }}
                        className="relative"
                      >
                        {/* The highlight is its own layer with a shared
                            layoutId, so Framer interpolates it BETWEEN rows:
                            arrowing down slides one plate rather than blinking
                            two. Behind the content, and aria-hidden — it is the
                            visual form of aria-selected, not extra meaning. */}
                        {active ? (
                          <motion.span
                            layoutId={reduceMotion ? undefined : `${listId}-active`}
                            aria-hidden
                            transition={{
                              duration: duration.fast,
                              ease: ease.standard,
                            }}
                            className="bg-surface-hover absolute inset-0 rounded-md"
                          />
                        ) : null}

                        <button
                          type="button"
                          id={`${listId}-${result.key}`}
                          role="option"
                          aria-selected={active}
                          // The press must land before the field's blur closes
                          // the popover out from under it, so selection runs on
                          // pointer-down rather than on click.
                          onPointerDown={(event) => {
                            event.preventDefault()
                            select(result)
                          }}
                          onMouseEnter={() => setHighlight(index)}
                          className={cn(
                            'relative flex w-full cursor-pointer items-center gap-2.5',
                            'rounded-md px-2.5 py-2 text-left',
                          )}
                        >
                          {/* Tinted only while active: at rest these three
                              glyphs are a quiet column, and the one that
                              matters picks up the brand with its row. */}
                          <span
                            className={cn(
                              'flex size-6 shrink-0 items-center justify-center rounded-md',
                              'transition-colors duration-[var(--dur-fast)]',
                              active
                                ? 'bg-brand-soft text-brand'
                                : 'bg-neutral-50 text-text-faint',
                            )}
                          >
                            <Icon size={13} aria-hidden />
                          </span>

                          <span className="flex min-w-0 flex-1 flex-col">
                            <span className="text-text-body truncate text-[13px] font-medium">
                              <Highlighted text={result.title} query={query} />
                            </span>
                            {result.subtitle ? (
                              <span className="text-text-muted truncate text-[11px]">
                                <Highlighted
                                  text={result.subtitle}
                                  query={query}
                                />
                              </span>
                            ) : null}
                          </span>

                          {/* The kind is named in words, not by the icon alone
                              — three glyphs at 13px are not a legend. */}
                          <span className="text-text-faint shrink-0 text-[11px]">
                            {t(`common.search.kind.${result.kind}`)}
                          </span>

                          <ResultStatus result={result} />

                          {/* The Enter affordance rides the highlighted row, so
                              the key that acts is shown on the thing it acts
                              on. Only on the active row: ten of these would be
                              decoration, one is an instruction. */}
                          <AnimatePresence initial={false}>
                            {active ? (
                              <motion.span
                                aria-hidden
                                initial={
                                  reduceMotion ? false : { opacity: 0, x: -3 }
                                }
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: duration.instant }}
                                className="text-text-faint hidden shrink-0 sm:block"
                              >
                                <CornerDownLeft size={12} />
                              </motion.span>
                            ) : null}
                          </AnimatePresence>
                        </button>
                      </motion.li>
                    )
                  })}
                </ul>

                {/* The key legend. Static, quiet, and the reason the arrow keys
                    are discoverable at all — nothing else on screen says the
                    list is navigable without the pointer. */}
                <div
                  aria-hidden
                  className="border-line-hair text-text-faint flex items-center gap-3 border-t px-3 py-1.5 text-[10px]"
                >
                  <span className="flex items-center gap-1">
                    <kbd className="border-border-subtle rounded border px-1 py-px font-sans">
                      ↑↓
                    </kbd>
                    {t('common.search.hintNavigate')}
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="border-border-subtle rounded border px-1 py-px font-sans">
                      ↵
                    </kbd>
                    {t('common.search.hintOpen')}
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="border-border-subtle rounded border px-1 py-px font-sans">
                      esc
                    </kbd>
                    {t('common.search.hintClose')}
                  </span>
                </div>
              </>
            )}
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* The count, for a screen reader that cannot see the list appear. */}
      <span aria-live="polite" className="sr-only">
        {showPopover && !isLoading
          ? t('common.search.resultCount', { count: results.length })
          : ''}
      </span>
    </div>
  )
}

export { SEARCH_MIN_LENGTH }
